import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import { NavData, SecondaryNavData } from '../Constant';
import NavItem from './NavItem';
import SessionsListSection from './SessionsListSection';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAgents } from '../../hooks';
import { cn } from '../../lib/utils';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [agentMenuPos, setAgentMenuPos] = useState({ top: 0, left: 0 });
  const agentMenuRef = useRef(null);
  const agentTriggerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useAgents();

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([{ agent_name: 'All', id: null }, ...(getAllAgentData?.data?.agents || [])]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
      setAgentMenuOpen(false);
      setAgentSearch('');
    }
  }, [collapsed]);

  useEffect(() => {
    if (!agentMenuOpen) return undefined;
    const handleClick = e => {
      if (agentMenuRef.current && !agentMenuRef.current.contains(e.target)) {
        setAgentMenuOpen(false);
        setAgentSearch('');
      }
    };
    const handleKey = e => {
      if (e.key === 'Escape') {
        setAgentMenuOpen(false);
        setAgentSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [agentMenuOpen]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agentData;
    return agentData.filter(a => a.agent_name?.toLowerCase().includes(q));
  }, [agentData, agentSearch]);

  const isActive = item => {
    const path = `/${item.path}`;
    if (item.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  const linkClass = (active, justifyCenter) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative',
      justifyCenter && 'justify-center',
      active ? 'bg-white/5 text-primary' : 'text-muted hover:text-white hover:bg-white/[0.03]',
    );

  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-shell-sidebar border-r border-shell-border flex flex-col h-screen sticky top-0 transition-all duration-200 z-50',
        collapsed ? 'w-16' : 'w-64',
      )}>
      <div
        className={cn(
          'h-16 flex items-center border-b border-shell-border flex-shrink-0',
          collapsed ? 'px-3 justify-center gap-1' : 'px-5 justify-between',
        )}>
        {collapsed ? (
          <Link to="/chat" className="text-base font-bold text-primary font-serif">
            S
          </Link>
        ) : (
          <Link to="/chat" className="text-sm font-bold tracking-wide font-serif text-white">
            SPLY LABS
          </Link>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-md text-muted-dim hover:text-white hover:bg-white/5"
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Scroll region: primary → chats */}
      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3">
        <ul className="space-y-0.5 px-2">
          {NavData.map(item => (
            <NavItem key={item.path || item.name} item={item} collapsed={collapsed} />
          ))}
        </ul>

        {!collapsed && (
          <div className="mt-6 px-2">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-xs uppercase tracking-wide text-muted-dim">Chats</span>
              <div className="relative" ref={agentMenuRef}>
                <button
                  ref={agentTriggerRef}
                  type="button"
                  onClick={() => {
                    const next = !agentMenuOpen;
                    if (next && agentTriggerRef.current) {
                      const rect = agentTriggerRef.current.getBoundingClientRect();
                      setAgentMenuPos({ top: rect.top, left: rect.right + 8 });
                    }
                    setAgentMenuOpen(next);
                  }}
                  className="flex items-center gap-1 text-xs text-muted-dim hover:text-white bg-transparent border-0 cursor-pointer p-0">
                  <span className="truncate max-w-[96px]">{selectedAgent?.label || 'All'}</span>
                  {agentMenuOpen ? (
                    <ChevronUp className="w-3 h-3 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  )}
                </button>
                {agentMenuOpen && (
                  <div
                    className="fixed z-[60] w-[200px] rounded-lg bg-[#161616] border border-[#2a2a2a] shadow-xl shadow-black/50 overflow-hidden"
                    style={{ top: agentMenuPos.top, left: agentMenuPos.left }}>
                    <div className="p-2.5 border-b border-[#2a2a2a]">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#606060] pointer-events-none" />
                        <input
                          autoFocus
                          value={agentSearch}
                          onChange={e => setAgentSearch(e.target.value)}
                          placeholder="Search agents..."
                          className="w-full bg-[#0e0e0e] border border-[#333] rounded-md pl-8 pr-2.5 py-1.5 text-xs text-white placeholder:text-[#606060] outline-none focus:border-[#444]"
                        />
                      </div>
                    </div>
                    <div className="max-h-[220px] overflow-y-auto p-1.5">
                      {filteredAgents.length === 0 ? (
                        <p className="px-2 py-3 text-xs text-[#707070] text-center">No agents found.</p>
                      ) : (
                        filteredAgents.map(data => {
                          const isSelected = (selectedAgent?.value || null) === (data?.id || null);
                          return (
                            <button
                              key={data?.id ?? 'all'}
                              type="button"
                              onClick={() => {
                                setSelectedAgent({ label: data?.agent_name, value: data?.id });
                                setAgentMenuOpen(false);
                                setAgentSearch('');
                              }}
                              className={cn(
                                'w-full text-left px-2.5 py-2 rounded-md text-xs transition-colors truncate',
                                isSelected
                                  ? 'bg-primary/15 text-primary'
                                  : 'text-[#a0a0a0] hover:bg-white/5 hover:text-white',
                              )}>
                              {data?.agent_name}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="max-h-[220px] overflow-y-auto overflow-x-hidden px-1">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}

      </nav>

      {/* Secondary nav: pinned to the bottom (non-scrolling) */}
      <div className="border-t border-shell-border py-2 px-2 flex-shrink-0">
        <ul className="space-y-0.5">
          {SecondaryNavData.map(item => {
            if (item.adminOnly && user?.plan?.type !== 'admin') return null;
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && item.name}
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={onLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted hover:text-white hover:bg-white/[0.03]',
                collapsed && 'justify-center',
              )}>
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Logout'}
            </button>
          </li>
        </ul>
      </div>

      <div
        className="border-t border-shell-border p-3 flex-shrink-0"
        onClick={() => navigate('/settings')}
        onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
        role="button"
        tabIndex={0}>
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/[0.03] cursor-pointer',
            collapsed && 'justify-center',
          )}>
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black text-sm font-bold flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{displayName}</p>
                <p className="text-xs text-[#808080]">Free Plan</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-faint" />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
